import React from "react";
import PropTypes from "prop-types";

// COMPONENTS
import FeatherIcon from "@components/FeatherIcon/FeatherIcon";

// HELPERS
import {createClassName, getClosestElementByQuery} from "@helpers/utils";
import i18n from "@helpers/i18n";


export default class Select extends React.PureComponent {
	static propTypes = {
		className: PropTypes.string,
		style: PropTypes.object,
		label: PropTypes.string,
		placeholder: PropTypes.string,
		inputPlaceholder: PropTypes.string,
		name: PropTypes.string,
		required: PropTypes.bool.isRequired,
		readOnly: PropTypes.bool.isRequired,
		options: PropTypes.array,
		selectedIndex: PropTypes.number.isRequired,
		validOnSelection: PropTypes.bool.isRequired,
		blurOnSelection: PropTypes.bool.isRequired,
		onChange: PropTypes.func,
		onOptionSelect: PropTypes.func
	};

	static defaultProps = {
		required: false,
		readOnly: false,
		selectedIndex: -1,
		validOnSelection: true,
		blurOnSelection: true
	};

	state = {
		focused: false,
		blurring: false,
		dropdownRect: null,
		options: null,
		inputValue: "",
		highlightedIndex: -1,
		selectedIndex: -1,
		selectedOption: null
	};

	get value() {
		return this.state.selectedOption;
	}

	componentDidMount() {
		const {props} = this;
		const {options, selectedIndex} = props;
		let newState = {options};

		if (options && selectedIndex >= 0) {
			newState = {...newState, selectedIndex, selectedOption: options[selectedIndex] || null};
		}

		this.setState(newState);
	}

	componentWillUnmount() {
		if (typeof this._blurringTimeout !== "undefined") {
			clearTimeout(this._blurringTimeout);
			this._blurringTimeout = -1;
		}
	}

	componentDidUpdate(prevProps, prevState) {
		const {state} = this;
		const newStateProps = {};

		if (prevProps.options !== this.props.options) {
			newStateProps.options = _getOptions.call(this);
		}

		if (prevProps.selectedIndex !== this.props.selectedIndex) {
			const options = newStateProps.options || this.props.options;

			if (options) {
				newStateProps.selectedIndex = this.props.selectedIndex;
				newStateProps.selectedOption = options[this.props.selectedIndex] || null;
			}
		}

		if (state.blurring && !prevState.blurring) {
			this._blurringTimeout = setTimeout(() => this.setState({blurring: false}), 100);
		}

		this.setState(newStateProps);
	}

	render() {
		const {props, state} = this;
		const {style, label, placeholder, inputPlaceholder, name, required, readOnly, validOnSelection} = props;
		const {focused, inputValue, highlightedIndex, selectedIndex, selectedOption} = state;
		const options = state.options || props.options;
		const disabled = props.disabled || !options || props.options.length === 0;
		const classes = createClassName(props.className, {
			"Select": true,
			"focus": focused,
			"blurring": state.blurring,
			"placeholder": !!placeholder || focused,
			"highlighting": highlightedIndex >= 0,
			"selected": selectedOption !== null,
			"valid": validOnSelection && selectedOption !== null,
			"required": required === true,
			"read-only": readOnly === true,
			"disabled": disabled
		});
		const renderSelectedOption = !!selectedOption && !inputValue ? (selectedOption.renderSelectedOption || props.renderSelectedOption) : null;

		return (
			<div ref={ref => this.containerElement = ref} className={classes} style={style}>
				<div className="Select-inner">
					<div ref={ref => this.infoElement = ref} className="Select-info"
						 onClick={!disabled ? this._onInfoClick : undefined}>
						{!!label && <div className="Select-label">{label}</div>}
						{!!selectedOption && !inputValue && (
							<div className="Select-selected">
								{renderSelectedOption ? renderSelectedOption(selectedOption, selectedIndex) : (selectedOption.label || selectedOption.value)}
							</div>
						)}
						{!!placeholder && !selectedOption && <div className="Select-placeholder">{placeholder}</div>}
					</div>
					<input
						ref={ref => this.input = ref}
						type="text"
						className="Select-input"
						placeholder={!selectedOption ? inputPlaceholder || placeholder || i18n("components", "search") : undefined}
						defaultValue={inputValue}
						required={required}
						disabled={disabled}
						onFocus={!disabled ? this._onInputFocus : undefined}
						onInput={!disabled ? this._onInput : undefined}
					/>
					{(!!name || (!!selectedOption && selectedOption.name)) && (
						<input
							type="hidden"
							name={selectedOption && selectedOption.name ? selectedOption.name : name}
							value={selectedOption ? selectedOption.value : ""}
							required={required}
							readOnly={readOnly}
							disabled={disabled}
						/>
					)}
					<div ref={ref => this.dropdownContainerElement = ref} className="Select-dropdown-container">
						<div
							ref={ref => this.dropdownElement = ref}
							className="Select-dropdown"
							// style={ state.dropdownRect && {
							//     width: state.dropdownRect.width,
							//     top: state.dropdownRect.top,
							//     left: state.dropdownRect.left,
							// }}
							onClick={!disabled ? this._onDropdownClick : undefined}
						>
							{!!options && options.length > 0 && options.map((option, index) => {
								const renderOption = option.renderOption || props.renderOption;
								return (
									<div
										key={`Select-dropdown-option-${index}`}
										className={createClassName("Select-dropdown-option", {
											"highlight": highlightedIndex === index,
											"disabled": option.disabled === true,
											"selected": selectedIndex === index,
											"custom": !!renderOption
										})}
										title={option.title || option.label}
									>
										{renderOption ? renderOption(option, index) : (option.label || option.value)}
									</div>
								);
							})}

							{(!options || options.length === 0) && (
								<div className="Select-dropdown-option not-found disabled"
									 title={i18n("components", "no_results_found")}>
									{i18n("components", "no_results_found")}
								</div>
							)}
						</div>
					</div>
					{validOnSelection && selectedOption !== null && (
						<div className="Select-status">
							<FeatherIcon type="check" size={20}/>
						</div>
					)}
					{(!validOnSelection || selectedOption === null) && (
						<div className="Select-arrow">
							<FeatherIcon type="chevron-down" size={26}/>
						</div>
					)}
				</div>
			</div>
		);
	}

	// Internal methods
	_onInfoClick = () => this.input.focus();

	_onInputFocus = () => {
		const {containerElement, dropdownContainerElement} = this;
		const dropdownRect = dropdownContainerElement.getBoundingClientRect();

		this.setState({focused: true, dropdownRect});

		// Body click event
		if (!this._bodyClickEvent) {
			document.body.addEventListener("click", this._bodyClickEvent = e => {
				const closestElement = getClosestElementByQuery(e.target, ".Select-inner");
				const isAncestor = !!closestElement && (closestElement === containerElement || containerElement.contains(closestElement));

				if (!isAncestor) {
					this._onBlur.call(this);
				}
			}, false);
		}

		// Body key up event
		if (!this._bodyKeyUpEvent) {
			document.body.addEventListener("keyup", this._bodyKeyUpEvent = e => this._onKeyPress.call(this, e), false);
		}

		// Document scroll event
		// if(!this._documentScrollEvent) {
		//     document.addEventListener("scroll", this._documentScrollEvent = e => {
		//         this.setState({ dropdownRect: dropdownContainerElement.getBoundingClientRect() });
		//     }, false);
		// }

		// Window resize event
		if (!this._windowResizeEvent) {
			window.addEventListener("resize", this._windowResizeEvent = e => {
				this.setState({dropdownRect: dropdownContainerElement.getBoundingClientRect()});
			}, false);
		}
	};

	_onBlur = () => {
		const {props} = this;
		const newStateProps = {
			options: props.options,
			focused: false,
			blurring: true,
			inputValue: "",
			highlightedIndex: -1
		};

		if (this._bodyClickEvent) {
			document.body.removeEventListener("click", this._bodyClickEvent);
			delete this._bodyClickEvent;
		}

		if (this._bodyKeyUpEvent) {
			document.body.removeEventListener("keyup", this._bodyKeyUpEvent);
			delete this._bodyKeyUpEvent;
		}

		// if(this._documentScrollEvent) {
		//     document.removeEventListener("scroll", this._documentScrollEvent);
		//     delete this._documentScrollEvent;
		// }

		if (this._windowResizeEvent) {
			window.removeEventListener("resize", this._windowResizeEvent);
			delete this._windowResizeEvent;
		}

		this.setState(newStateProps);
	};

	_onInput = (e) => {
		const {props} = this;
		const options = _getOptions.call(this);
		const inputValue = this.input.value;
		const newStateProps = {
			options,
			inputValue,
			highlightedIndex: options.length > 0 ? 0 : -1
		};

		this.setState(newStateProps, () => {
			const {onInput} = props;
			if (onInput) {
				onInput(inputValue);
			}
		});
	};

	_onDropdownClick = (e) => {
		const {props, state} = this;
		const prevSelectedFilteredIndex = state.selectedIndex;
		const closestOptionElement = getClosestElementByQuery(e.target, ".Select-dropdown-option");
		const selectedFilteredIndex = (closestOptionElement && !closestOptionElement.classList.contains("disabled") ?
				Array.from(closestOptionElement.parentNode.children).indexOf(closestOptionElement) :
				-1
		);

		if (selectedFilteredIndex >= 0) {
			const selectedOption = state.options[selectedFilteredIndex];
			const selectedIndex = props.options.indexOf(selectedOption);

			if (props.onOptionSelect) {
				props.onOptionSelect(state.options[selectedFilteredIndex], selectedIndex);
			}

			if (selectedFilteredIndex !== prevSelectedFilteredIndex) {
				if (props.onChange) {
					props.onChange(state.options[selectedFilteredIndex], selectedIndex);
				}
			}
		}

		// Call onBlur event
		if (props.blurOnSelection) {
			this._onBlur();
		}
	};

	_onKeyPress = (e) => {
		const {key, target} = e;
		const {props, state} = this;
		const {options} = state;
		const newState = {};
		let newStateCallback;

		if (key === "Tab" && target !== this.input) {
			this.setState({focused: false, blurring: true, inputValue: "", highlightedIndex: -1});
		}
		else if (key === "ArrowUp") {
			const {selectedIndex, highlightedIndex: prevHighlightedIndex} = state;
			const highlightedIndex = newState.highlightedIndex = Math.max((prevHighlightedIndex >= 0 ? prevHighlightedIndex : selectedIndex) - 1, 0);

			newStateCallback = () => {
				const selectedOptionElement = this.dropdownElement.children[highlightedIndex];
				_ensureOptionInView.call(this, selectedOptionElement);
			};
		}
		else if (key === "ArrowDown") {
			const {selectedIndex, highlightedIndex: prevHighlightedIndex} = state;
			const highlightedIndex = newState.highlightedIndex = Math.min((prevHighlightedIndex >= 0 ? prevHighlightedIndex : selectedIndex) + 1, options.length - 1);

			newStateCallback = () => {
				const selectedOptionElement = this.dropdownElement.children[highlightedIndex];
				_ensureOptionInView.call(this, selectedOptionElement);
			};
		}
		else if (key === "Enter") {
			const {options: optionsAll, blurOnSelection} = props;
			const {highlightedIndex: prevHighlightedIndex} = state;
			const selectedOption = options[prevHighlightedIndex] || null;
			const newSelectedIndex = optionsAll.indexOf(selectedOption);

			if (blurOnSelection) {
				newState.options = optionsAll;
				newState.focused = false;
				newState.inputValue = "";
				newState.highlightedIndex = -1;
			}

			newStateCallback = () => {
				const {onChange, onOptionSelect} = props;
				if (onChange) {
					onChange(selectedOption, newSelectedIndex);
				}
				if (onOptionSelect) {
					onOptionSelect(selectedOption, newSelectedIndex);
				}
			};
		}

		this.setState(newState, newStateCallback);

		// Remove event listener from body
		if (target !== this.input) {
			document.body.removeEventListener("keyup", this._bodyKeyUpEvent);
			delete this._bodyKeyUpEvent;
		}
	};
}


// PRIVATE FUNCTIONS
function _getOptions() {
	const {props, input: {value: input}} = this;
	const {options: optionsAll} = props;

	if (input.length > 0) {
		const filterer = (option) => option.label.toLowerCase().indexOf(input.toLowerCase()) === 0;
		const customFilterer = props.customFilterer ? (option, index, options) => props.customFilterer({
			option,
			index,
			options
		}, input) : null;

		return optionsAll.filter(customFilterer || filterer);
	}
	else {
		return optionsAll;
	}
}

function _ensureOptionInView(optionElement) {
	const {dropdownElement} = this;

	// Determine dropdownElement top and bottom
	const cTop = dropdownElement.scrollTop;
	const cBottom = cTop + dropdownElement.clientHeight;

	// Determine optionElement top and bottom
	const eTop = optionElement.offsetTop;
	const eBottom = eTop + optionElement.clientHeight;

	// Check if out of view
	if (eTop < cTop) {
		dropdownElement.scrollTop -= (cTop - eTop);
	}
	else if (eBottom > cBottom) {
		dropdownElement.scrollTop += (eBottom - cBottom);
	}
}


// WEBPACK FOOTER //
// ./src/components/Select/Select.js